import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, FileText, User, Sparkles, Heart } from 'lucide-react';
import { getHomeDashboardRoute } from '../../utils/navigation';
import { useBranding } from '../../contexts/BrandingContext';
import EazySparkIcon from '../shared/EazySparkIcon';

/**
 * BottomNavigation — Tenant-aware Mobile Bottom Navigation
 *
 * Phase 1: Dynamic branding from BrandingContext
 *
 * Five tabs: Home · Browse · Apps · AI Assistant · Profile
 *
 * Active state uses tenant primary color
 *
 * CSS:
 * - Background: Platform surface color with backdrop blur
 * - Border: Border color from design tokens
 * - Padding: 10px 4px 20px (20px bottom = safe area on iPhone)
 */
const BottomNavigation = ({ userRole = 'student' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding();

  // AI assistant tab label based on platform
  const aiTabLabel = branding.platform_name === 'EazyCourseEngine'
    ? 'Eazy'
    : branding.platform_name === '∑n-route'
    ? '∑n'
    : 'Guru';

  // Student tabs: Home, Browse, Saved, Apps, AI Assistant, Profile
  const studentNavItems = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/dashboard',
      matchPaths: ['/', '/home', '/dashboard'],
    },
    {
      id: 'browse',
      label: 'Browse',
      icon: Search,
      path: '/programs',
      matchPaths: ['/programs', '/browse'],
    },
    {
      id: 'saved',
      label: 'Saved',
      icon: Heart,
      path: '/saved-programs',
      matchPaths: ['/saved-programs', '/shortlist'],
    },
    {
      id: 'apps',
      label: 'Apps',
      icon: FileText,
      path: '/applications',
      matchPaths: ['/applications', '/apps'],
    },
    {
      id: 'ai',
      label: aiTabLabel,
      icon: EazySparkIcon,
      isCustomIcon: true,
      path: '/ama/explore',
      matchPaths: ['/ama/explore', '/ai/explore', '/eazy'],
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      path: '/profile',
      matchPaths: ['/profile', '/settings'],
    },
  ];

  // Partner tabs (operations only - no analytics)
  const partnerNavItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/partner-dashboard', matchPaths: ['/partner-dashboard'] },
    { id: 'students', label: 'Students', icon: User, path: '/partner/students', matchPaths: ['/partner/students'] },
    { id: 'apps', label: 'Apps', icon: FileText, path: '/partner/applications', matchPaths: ['/partner/applications'] },
    { id: 'profile', label: 'Profile', icon: User, path: '/tenant/settings', matchPaths: ['/tenant/settings'] },
  ];

  // Tenant Admin tabs (partner access + analytics)
  const tenantAdminNavItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/partner-dashboard', matchPaths: ['/partner-dashboard'] },
    { id: 'students', label: 'Students', icon: User, path: '/partner/students', matchPaths: ['/partner/students'] },
    { id: 'apps', label: 'Apps', icon: FileText, path: '/partner/applications', matchPaths: ['/partner/applications'] },
    { id: 'analytics', label: 'Analytics', icon: Sparkles, path: '/analytics', matchPaths: ['/analytics'] },
    { id: 'profile', label: 'Profile', icon: User, path: '/tenant/settings', matchPaths: ['/tenant/settings'] },
  ];

  // Superadmin tabs
  const superadminNavItems = [
    { id: 'home', label: 'Home', icon: Home, path: getHomeDashboardRoute(), matchPaths: [getHomeDashboardRoute()] },
    { id: 'tenants', label: 'Tenants', icon: User, path: '/admin/tenants', matchPaths: ['/admin/tenants'] },
    { id: 'users', label: 'Users', icon: FileText, path: '/admin/users', matchPaths: ['/admin/users'] },
    { id: 'analytics', label: 'Analytics', icon: Sparkles, path: '/analytics', matchPaths: ['/analytics'] },
    { id: 'settings', label: 'Settings', icon: User, path: '/admin/ai-config', matchPaths: ['/admin/ai-config'] },
  ];

  const getNavItems = () => {
    switch(userRole) {
      case 'superadmin':
        return superadminNavItems;
      case 'tenant_admin':
        return tenantAdminNavItems;
      case 'partner':
        return partnerNavItems;
      default:
        return studentNavItems;
    }
  };

  const navItems = getNavItems();

  const isActive = (tab) => {
    if (!tab.matchPaths) {
      return location.pathname === tab.path || location.pathname.startsWith(tab.path + '/');
    }
    return tab.matchPaths.some((p) => {
      if (p === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(p);
    });
  };

  return (
    <nav
      className="
        fixed bottom-0 left-0 right-0 z-[100] md:hidden
        bg-ece-surface dark:bg-[rgba(6,13,24,0.92)]
        border-t border-[rgba(10,22,40,0.08)] dark:border-[rgba(255,255,255,0.07)]
        pt-[10px] pb-[calc(20px+env(safe-area-inset-bottom,0px))] px-1
        flex
        backdrop-blur-[20px]
        shadow-[0_-1px_0_rgba(10,22,40,0.08)] dark:shadow-[0_-1px_0_rgba(255,255,255,0.07)]
      "
    >
      {navItems.map((tab) => {
        const active = isActive(tab);
        const IconComponent = tab.icon;

        return (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            style={active ? { color: branding.primary_color } : undefined}
            className={`
              flex-1 flex flex-col items-center gap-[3px]
              cursor-pointer pt-[2px] relative
              transition-colors duration-150
              ${active ? '' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}
            `}
            aria-label={tab.label}
            aria-current={active ? 'page' : undefined}
          >
            {/* Active indicator bar */}
            <span
              style={active ? { backgroundColor: branding.primary_color } : {}}
              className={`
                absolute -top-[10px]
                h-[2px] rounded-b-sm
                transition-all duration-200
                ${active ? 'w-[22px]' : 'w-0'}
              `}
            />

            {/* Icon */}
            {tab.isCustomIcon ? (
              <IconComponent size={20} />
            ) : (
              <IconComponent size={20} strokeWidth={1.8} />
            )}

            {/* Label */}
            <span className={`text-[9.5px] tracking-[0.03em] ${active ? 'font-bold' : 'font-medium'}`}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNavigation;
